/**
 * STORE-V2-CONVERSION-004 — brand directory index from canonical catalog (no live scrape).
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { BrandIndexRow } from "./catalog-v2-data.js";
import {
  CANONICAL_ALIASES,
  normalizeBrandName as sharedNormalizeBrandName,
  normalizeBrandQuery,
} from "./brand-normalization-v2.js";
import { resolveBrandLogo, textFallbackLogoRecord, type BrandLogoRecord } from "./brand-logo-registry-v2.js";

export interface CatalogBrand {
  brand_id: string;
  name: string;
  slug: string;
  aliases: string[];
  brand_country: string | null;
  productCount: number;
  activeAvailabilityCount: number;
  logo: BrandLogoRecord | null;
}

function compactBrand(value: string | null | undefined): string {
  return String(value ?? "").normalize("NFKC").replace(/\s+/g, " ").trim();
}

/** Packaging/size/qty labels mistaken as brands — exclude from brand directory (STAGING-LAUNCH-READINESS-002-R1). */
export function isNoiseBrandName(name: string | null | undefined): boolean {
  const raw = compactBrand(name);
  if (!raw) return true;
  const lower = raw.toLocaleLowerCase("uk");
  if (/^(без\s*бренд|no\s*brand|unknown\s*brand|unbranded|нема\s*бренд)/i.test(lower)) return true;
  if (/продукц(ия|ія)\s+как\s+она/i.test(lower)) return true;
  if (/^\d+$/.test(raw.replace(/\s/g, ""))) return true;
  if (/^\d+\s*(пар|пара|шт|штук|pcs|pc|pack|уп|упак)/i.test(lower)) return true;
  if (/^\d+\s*[x×х*]\s*\d+/i.test(raw)) return true;
  if (/^[\d.,]+\s*(см|mm|ml|л|l|g|г|кг|kg|м|m|cm)\b/i.test(lower)) return true;
  if (/^\d+\s*[a-zа-я]{1,2}$/i.test(raw)) return true;
  if (/^[\d.,]+\s*[×xх*]\s*[\d.,]+\s*(см|mm|cm|м|m)?$/i.test(raw)) return true;
  if (/^(s|m|l|xl|xxl|xs)$/i.test(raw)) return true;
  return false;
}

export function normalizeBrandName(value: string | null | undefined): string | null {
  return sharedNormalizeBrandName(value) ?? (compactBrand(value) || null);
}

export function brandSlug(name: string): string {
  return (
    name
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLocaleLowerCase("en")
      .replace(/[^a-z0-9\u0400-\u04ff]+/g, "-")
      .replace(/^-+|-+$/g, "") || `brand-${simpleHash(name)}`
  );
}

function simpleHash(value: string): string {
  let hash = 2166136261;
  for (const char of value) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return (hash >>> 0).toString(36);
}

/** Known brand → brand-country (positioning), never claimed as manufacturing origin. */
const BRAND_COUNTRY_HINTS: Record<string, string> = {
  kikkoman: "Japan",
  "aroy-d": "Thailand",
  "royal-family": "Taiwan",
  "lucky-me": "Philippines",
  nongshim: "South Korea",
  samyang: "South Korea",
  koka: "Singapore",
  nissin: "Japan",
  ottogi: "South Korea",
  cj: "South Korea",
  mama: "Thailand",
  indomie: "Indonesia",
  "bio-planete": "France",
  "lee-kum-kee": "Hong Kong",
  "yeo-s": "Singapore",
  "chung-jung-one": "South Korea",
  "daisho": "Japan",
  "marukome": "Japan",
  "s&b": "Japan",
  "house-foods": "Japan",
  "ajinomoto": "Japan",
  "wang": "South Korea",
  "paldo": "South Korea",
  "jinro": "South Korea",
  "lobo": "Thailand",
  "squid": "Thailand",
  "teo": "Vietnam",
  "vina": "Vietnam",
};

/** Title tokens → display brand name when `product.brand` is empty in snapshot. */
const TITLE_BRAND_HINTS: Array<{ re: RegExp; name: string }> = [
  { re: /\bkikkoman\b/i, name: "Kikkoman" },
  { re: /\bnongshim\b|\b농심\b/i, name: "Nongshim" },
  { re: /\bsamyang\b/i, name: "Samyang" },
  { re: /\bindomie\b/i, name: "Indomie" },
  { re: /\bmama\b/i, name: "Mama" },
  { re: /\bnissin\b/i, name: "Nissin" },
  { re: /\bottogi\b/i, name: "Ottogi" },
  { re: /\baroy[\s-]?d\b/i, name: "Aroy-D" },
  { re: /\blee\s*kum\s*kee\b/i, name: "Lee Kum Kee" },
  { re: /\bkoka\b/i, name: "Koka" },
  { re: /\blucky\s*me\b/i, name: "Lucky Me" },
  { re: /\bbio\s*plan[eè]te\b/i, name: "Bio Planète" },
  { re: /\bajinomoto\b/i, name: "Ajinomoto" },
  { re: /\bpaldo\b/i, name: "Paldo" },
  { re: /\blobo\b/i, name: "Lobo" },
];

function inferBrandFromTitle(title: string | null | undefined): string | null {
  const t = String(title ?? "");
  if (!t) return null;
  for (const hint of TITLE_BRAND_HINTS) {
    if (hint.re.test(t)) return hint.name;
  }
  return null;
}

function packagingDerivedLogo(_slug: string, _name: string, _imageUrl: string | null): BrandLogoRecord | null {
  // Intentionally unused: product thumbnails must never be brand logos (STAGING-LAUNCH-READINESS-002).
  return null;
}
void packagingDerivedLogo;

function aliasesForCanonical(name: string): string[] {
  const direct = CANONICAL_ALIASES[name];
  if (direct) return [...direct];
  const key = Object.keys(CANONICAL_ALIASES).find(
    (k) => normalizeBrandQuery(k) === normalizeBrandQuery(name),
  );
  return key ? [...(CANONICAL_ALIASES[key] ?? [])] : [];
}

function brandCountryFor(slug: string, sampleCountry: string | null): string | null {
  return BRAND_COUNTRY_HINTS[slug] ?? sampleCountry ?? null;
}

export function buildBrandIndex(products: CatalogV2Product[]): CatalogBrand[] {
  const buckets = new Map<
    string,
    {
      name: string;
      count: number;
      active: number;
      sampleCountry: string | null;
      sampleImage: string | null;
    }
  >();
  for (const product of products) {
    const name = normalizeBrandName(product.brand) ?? inferBrandFromTitle(product.title);
    if (!name || isNoiseBrandName(name)) continue;
    const key = name.toLocaleLowerCase("en");
    const current = buckets.get(key) ?? {
      name,
      count: 0,
      active: 0,
      sampleCountry: null,
      sampleImage: null,
    };
    current.count += 1;
    if (product.isSellable && product.stockStatus !== "out_of_stock") current.active += 1;
    if (!current.sampleCountry && product.country) current.sampleCountry = product.country;
    if (!current.sampleImage && product.imageUrl) current.sampleImage = product.imageUrl;
    buckets.set(key, current);
  }
  return [...buckets.values()]
    .map(({ name, count, active, sampleCountry, sampleImage }) => {
      const slug = brandSlug(name);
      const verified = resolveBrandLogo(slug);
      return {
        brand_id: slug,
        name,
        slug,
        aliases: aliasesForCanonical(name),
        brand_country: brandCountryFor(slug, sampleCountry),
        productCount: count,
        activeAvailabilityCount: active,
        // STAGING-LAUNCH-READINESS-002 — never use product thumbnails as brand logos.
        logo: verified ?? textFallbackLogoRecord(slug, name),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, "uk", { sensitivity: "base", numeric: true }));
}

export function productsForBrand(products: CatalogV2Product[], slug: string): CatalogV2Product[] {
  return products.filter((product) => {
    if (!product.isSellable) return false;
    const name = normalizeBrandName(product.brand) ?? inferBrandFromTitle(product.title);
    return name != null && brandSlug(name) === slug;
  });
}

export function mapBrandIndexRows(rows: BrandIndexRow[]): CatalogBrand[] {
  return rows.map((row) => {
    const slug = row.slug || brandSlug(row.name);
    const verified = resolveBrandLogo(slug);
    return {
      brand_id: row.brand_id || slug,
      name: row.name,
      slug,
      aliases: aliasesForCanonical(row.name),
      brand_country: row.brand_country,
      productCount: row.productCount,
      activeAvailabilityCount: row.productCount,
      logo: verified ?? textFallbackLogoRecord(slug, row.name),
    };
  });
}
